import React, { useEffect, useState, CSSProperties, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { isStreamingContentAtom, isStreamingMapAtom, pass1LearningPathAtom, pass2LearningPathAtom } from '@/jotaiStore';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle2 } from 'lucide-react';

// CSS for shimmer effect
const shimmerStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1s infinite linear',
};

const MapGenerationProgress = () => {
  const isStreaming = useAtomValue(isStreamingMapAtom);
  const pass1Path = useAtomValue(pass1LearningPathAtom);
  const pass2Path = useAtomValue(pass2LearningPathAtom);
  const isStreamingContent = useAtomValue(isStreamingContentAtom);
  
  const [showProgress, setShowProgress] = useState(false);
  const [progressLabel, setProgressLabel] = useState('');
  const [progressValue, setProgressValue] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [sideQuestCount, setSideQuestCount] = useState(0);
  const prevPass2PathRef = useRef<string>('');

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isStreaming) {
      setShowProgress(true);
      setProgressLabel('Generating map');
      setProgressValue(0);
      setIsComplete(false);
      setSideQuestCount(0); // Reset counter when starting a new stream
      prevPass2PathRef.current = ''; // Reset previous path ref
    } else if (showProgress) {
      setProgressLabel('Complete');
      setProgressValue(100);
      setIsComplete(true);
      timeoutId = setTimeout(() => {
        setShowProgress(false);
        setIsComplete(false);
      }, 1500);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isStreaming]);

  useEffect(() => {
    if (pass1Path && pass1Path.length > 0 && !isComplete) {
      setProgressLabel(`Generating topic ${pass1Path.length}`);
      setProgressValue(Math.min(7 * pass1Path.length, 70));
    }
  }, [pass1Path?.length, isComplete]);

  useEffect(() => {
    if (pass2Path && pass2Path.length > 0 && !isComplete) {
      // Stringify the current path to compare with previous
      const currentPathString = JSON.stringify(pass2Path);
      
      // Check if the path has changed
      if (currentPathString !== prevPass2PathRef.current) {
        // Update our reference to the current path
        prevPass2PathRef.current = currentPathString;
        
        // Increment the counter
        setSideQuestCount(prevCount => {
          const newCount = prevCount + 1;
          setProgressLabel(`Generating sidequests for topic ${newCount}`);
          setProgressValue(60 + Math.min(6 * newCount, 30));
          return newCount;
        });
      }
    }
  }, [pass2Path, isComplete]);

  return (
    <AnimatePresence>
      {showProgress && (
        <motion.div
          initial={{ opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
          exit={{ opacity: 0, height: 0, marginTop: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center gap-2">
              <motion.div
                key={progressLabel}
                initial={{ opacity: 0, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 0 }}
              >
                <span className="text-[15px] text-colors-green">
                  {progressLabel}
                </span>
              </motion.div>
              <AnimatePresence mode="wait">
                {isComplete ? (
                  <motion.div
                    key="check"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <CheckCircle2 className="w-4 h-4 text-colors-green" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="spinner"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Loader2 className="w-3.5 h-3.5 text-colors-green animate-spin" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <span className="text-base text-colors-green">{progressValue}%</span>
          </div>
          <div className="w-full bg-shades-text/10 rounded-full h-1.5">
            <div className="relative w-full h-1.5 rounded-full overflow-hidden bg-shades-baseDark">
              <div 
                className="h-full bg-colors-green rounded-full overflow-hidden"
                style={{ 
                  width: `${progressValue}%`,
                  position: 'relative'
                }}
              >
                <div style={shimmerStyle} />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MapGenerationProgress; 